import { Radio } from "lucide-react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCanMutateOpsTool } from "@/hooks/use-ops-access";
import { uiStore } from "@/lib/stores/ui-store";

/**
 * Admin-only entry point into Broadcast Controls, the panel where a broadcast is
 * started ("Go Live"). Always visible to broadcast-capable admins, whether or
 * not a broadcast is active. The live-broadcast controls (`BroadcastStatus`, the
 * rail "LIVE" button) only appear once a broadcast exists. Restores the entry
 * the in-sidebar ops panel used to provide (`canSeeBroadcast`) after the ops
 * tools moved to /operations.
 *
 * Shown on the `broadcast` tool's `moderate` grant (TECH-3239), exactly what
 * every procedure this panel drives requires, rather than on a role-name list.
 * Only one adapter seeds those role names, and such a list could drift from the
 * gate without anything failing.
 */
export function BroadcastControlsButton({
	collapsed = false,
}: {
	collapsed?: boolean;
}) {
	const canBroadcast = useCanMutateOpsTool("broadcast");
	const navigate = useStore(uiStore, (s) => s.navigate);

	if (!canBroadcast) return null;

	const open = () => navigate({ type: "broadcastControls" });

	if (collapsed) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Button className="size-9" onClick={open} size="icon" variant="ghost">
						<Radio className="size-4" />
					</Button>
				</TooltipTrigger>
				<TooltipContent side="right">Broadcast Controls</TooltipContent>
			</Tooltip>
		);
	}

	return (
		<div className="rounded-lg border bg-card p-3">
			<Button
				className="!px-1 h-auto w-full justify-start gap-2 py-1.5"
				onClick={open}
				variant="ghost"
			>
				<Radio className="size-3.5" />
				<span className="font-medium text-sm">Broadcast Controls</span>
			</Button>
		</div>
	);
}
